import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import SystemLabel from '../../components/systemlabel/SystemLabel';
import { inputValidator } from '../../utils/validation.utils';
import { basedAxios } from '../../store/Store';
import Spinner from '../../components/Spinner';
import useOnClickOutside from '../../hooks/useOnClickOutside';

const CalendarInvitation = observer(props => {
  const [showDialog, setShowDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [email, setEmail] = useState('');

  const dialogRef = useRef(null);

  const { settings, environment, orders, activePersonNo } = props;
  const { texts } = settings;

  const prefix = window.location.host.includes('localhost') ? '' : '/cview';
  const badgeNo = orders?.[activePersonNo]?.badgeNo;

  const orderEmail = useMemo(() => orders?.[activePersonNo]?.person?.email, [orders, activePersonNo]);

  useEffect(() => {
    if (email === '' && orderEmail) {
      setEmail(orderEmail);
    }
  }, [orderEmail]);

  const handleSendCalendarInvitation = async () => {
    if (email === '') return;

    setIsSending(true);

    try {
      const searchParams = new URLSearchParams();
      searchParams.append('email', email);
      searchParams.append('badgeNo', badgeNo ?? '');

      await basedAxios.get(`./calendar/geticalfile/${settings.invitationId}?${searchParams.toString()}`);

      setEmail('');
      setIsSent(true);

      const timeout = setTimeout(() => {
        setIsSent(false);
        clearTimeout(timeout);
      }, 3000);
    } catch (error) {
    } finally {
      setIsSending(false);
    }
  };

  const isEmailValid = inputValidator(email, 'email');

  useOnClickOutside(
    [
      {
        current: dialogRef.current  
      }
    ],
    () => {
      setShowDialog(false);
      setIsSent(false);
      setIsSending(false);
    },
    [showDialog]
  );

  return (
    <>
      <button onClick={() => setShowDialog(true)} className="cms-button">
        {texts[environment.lang]}
      </button>
      <dialog
        ref={dialogRef}
        open={showDialog}
        className="calendar-invitation-dialog bg-white p-4 z-50 border rounded-md border-slate-200 translate-y-12  max-w-lg w-full"
      >
        <button onClick={() => setShowDialog(false)} className="absolute top-2 right-2 p-0 border-0 bg-transparent">
          <i className="ri-close-line"></i>
        </button>
        <div className="flex flex-col gap-2">
          <label>
            <SystemLabel name="CalendarEmail" />
          </label>
          <input
            value={email}
            onInput={e => setEmail(e.target.value)}
            type="email"
            placeholder="example@domain.com"
            className={'border rounded-md border-slate-300 ' + (email === '' ? '' : isEmailValid ? 'border-teal-600' : 'border-red-600')}
          />
          <caption className="text-sm text-start italic text-slate-600">
            <SystemLabel name="CalendarInvitationEmailCaption" />
          </caption>
        </div>
        <div className="mt-4 flex items-start gap-2 w-full">
          <button
            disabled={email === ''}
            onClick={handleSendCalendarInvitation}
            className={`bg-teal-600 border border-slate-200  flex items-center text-white rounded-md ${isEmailValid ? '' : 'opacity-50'}`}
          >
            {isSending ? (
              <Spinner className="custom-spinner" />
            ) : isSent ? (
              <i className="ri-check-line mr-1"></i>
            ) : (
              <i className="ri-send-plane-2-line mr-1"></i>
            )}
            <SystemLabel name="Send" />
          </button>
          <a href={`${prefix}/api/calendar/geticalfile/${settings.invitationId}?badgeNo=${badgeNo ?? ''}`} download className="no-underline">
            <button className="bg-white flex items-center border border-slate-200 text-slate-800 hover:bg-slate-100 hover:border-slate-300 rounded-md">
              <i className="ri-download-2-line mr-1"></i>
              <SystemLabel name="Download" />
            </button>
          </a>
        </div>
      </dialog>
    </>
  );
});

export default CalendarInvitation;
